import { useState } from 'react';

import PreviewIcon from '@mui/icons-material/Preview';
import { Box, Button as MuiButton, ButtonGroup, CircularProgress, Grid, Typography } from '@mui/material';
import { Labeled, useGetOne } from 'react-admin';
import { useWatch } from 'react-hook-form';
import { AccountTypeDto, OfferDetailsDto, OfferDocumentTypeDto } from 'src/dtos';
import { FileViewerModal, ErrorMessageField } from 'src/lib';
import { DownloadedFile } from 'src/models';
import { fillSubscriptionAgreement } from 'src/utils';

const PREVIEWABLE_ACCOUNT_TYPES: { type: AccountTypeDto; label: string }[] = [
  { type: AccountTypeDto.Individual, label: 'Individual' },
  { type: AccountTypeDto.Joint, label: 'Joint' },
  { type: AccountTypeDto.Ugma, label: 'UGMA' },
  { type: AccountTypeDto.Retirement, label: 'Retirement' },
  { type: AccountTypeDto.Entity, label: 'Entity' },
];

export const SubscriptionAgreementPreviewButton = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [previewFile, setPreviewFile] = useState<DownloadedFile | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const selectedDocumentType = useWatch({ name: 'type' });
  const uploadedFiles = useWatch({ name: '_files' });
  const offerId = useWatch({ name: 'offerId' });

  const { data: offer } = useGetOne<OfferDetailsDto>('offers', { id: offerId }, { enabled: Boolean(offerId) });

  // Preview with the smallest order the offer actually allows, priced at the offer's real price per share.
  const quantity = offer ? offer.minimumInvestShareQty + 1 : undefined;
  const totalCost = offer && quantity ? quantity * offer.pricePerShare : undefined;

  const handlePreview = async (accountType: AccountTypeDto) => {
    setErrorMessage(null);

    try {
      setIsLoading(true);

      // Get the file from the form
      const file = Array.isArray(uploadedFiles) ? uploadedFiles[0]?.rawFile : uploadedFiles?.rawFile;

      if (!file) {
        setErrorMessage('File upload is required');

        return;
      }

      // Fill and flatten the PDF using the same field-mapping rules the worker uses in production
      const { url } = await fillSubscriptionAgreement({ file, accountType, quantity, totalCost });

      // Set up preview
      setPreviewFile({
        url,
        type: 'pdf',
        name: `preview-${accountType}.pdf`,
      });
    } catch (error: any) {
      setErrorMessage(`Error generating preview: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClosePreview = () => {
    if (previewFile?.url) {
      URL.revokeObjectURL(previewFile.url);
    }
    setPreviewFile(null);
  };

  if (selectedDocumentType !== OfferDocumentTypeDto.SUBSCRIPTION_AGREEMENT) {
    return null;
  }

  return (
    <>
      <Grid item xs={12}>
        <Labeled label='Preview' color={errorMessage ? 'error' : undefined}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <ButtonGroup variant='outlined' size='medium' disabled={isLoading}>
                {PREVIEWABLE_ACCOUNT_TYPES.map(({ type, label }) => (
                  <MuiButton
                    key={type}
                    startIcon={isLoading ? <CircularProgress size={16} /> : <PreviewIcon />}
                    onClick={() => handlePreview(type)}>
                    {label}
                  </MuiButton>
                ))}
              </ButtonGroup>
              {isLoading && (
                <Typography variant='caption' color='text.secondary'>
                  Generating preview...
                </Typography>
              )}
            </Box>
            {errorMessage && <ErrorMessageField error={errorMessage} />}
          </Box>
        </Labeled>
      </Grid>
      <Grid item xs={12}>
        <Typography variant='subtitle2' color='text.secondary'>
          *All preview accounts are non-FINRA members and accredited (except UGMA, which previews as non-accredited);
          Joint adds a second John/Jane Doe account holder, Retirement previews as a Traditional IRA, and Entity
          previews as an LLC (C-Corporation) named &quot;Acme Investment Holdings, LLC&quot;.
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Typography variant='subtitle2' color='text.secondary'>
          *The preview will be generated with the smallest order the offer actually allows (minimum invest share
          quantity plus one share), priced at the offer&apos;s real price per share. If no offer pricing is found then a
          quantity of 1000 shares with a total cost of $5,000 will be used for the preview.
        </Typography>
      </Grid>
      {previewFile && <FileViewerModal file={previewFile} onClose={handleClosePreview} />}
    </>
  );
};
